/**
 * THE SIGN-IN WIRE, AND THE ONLY PLACE IT IS KNOWN.
 *
 * Paths and request bodies are typed from `src/api/generated/pharmos-api.ts`,
 * so a revision to the backend's document fails here at compile time rather
 * than inside a screen. The RESPONSE is validated with zod as well, because the
 * document declares every payload member optional (`payload?`, `authToken?`)
 * and a screen cannot be written against "possibly absent everything": a body
 * that does not match is a CONTRACT error, which is a truer thing to say than
 * rendering half a session.
 *
 * THE ENVELOPE. Every success is `{ success, code, message, payload }`. The
 * envelope's own `code`/`message` are the backend's, not ours; nothing here
 * switches on `code`. The screen prints `message` (`docs/DECISIONS.md`, 24-09-2026).
 *
 * THE ORDER, from the document's own descriptions:
 *   1. POST /v1/user/login              → interim token, the branches, isPasswordExpired
 *   2. POST /v1/user/login/reset-password   (only when isPasswordExpired)
 *   3. POST /v1/user/login/branches/{code}  → the working token
 *
 * EVERY PASSWORD FIELD IS SENT ENCRYPTED (`encryptPassword`, `api/password-cipher.ts`):
 * the plain password lives only in the form that typed it and this call.
 * THE LOGIN PASSWORD IS THE ONLY ONE WITH A TIMESTAMP sealed in front of it.
 * The reset's new password, and both fields of a change, are the password alone.
 */
import { z } from "zod/mini";
import { accountStatusSchema, displayLangSchema, screenLayoutSchema } from "@/api/account";
import { branchProgrammesSchema } from "@/api/branch-programme";
import { createApiClient } from "@/api/client";
import { modulesPermissionsSchema } from "@/api/modules-permissions";
import { encryptPassword } from "@/api/password-cipher";
import { payloadOf } from "@/api/envelope";
import type { paths } from "@/api/generated/pharmos-api";
import type { DisplayLang, ScreenLayout } from "@/api/preference";

/**
 * Built per call. `apiBaseUrl()` is read then, which is what lets a test stub
 * the host; a client built at import would keep the empty URL from load time.
 */
function client() {
  return createApiClient<paths>();
}

/* ── the document's schemas ──────────────────────────────────────────────── */

const roleSchema = z.object({
  roleCode: z.optional(z.string()),
  roleNameEn: z.optional(z.string()),
  roleNameZh: z.optional(z.string()),
});

const grantedBranchSchema = z.object({
  /** Required here: a branch card without a code cannot be entered. The document leaves it optional (`GAPS.md` B6). */
  branchCode: z.string(),
  branchNameEn: z.optional(z.string()),
  branchNameZh: z.optional(z.string()),
  /** Parsed. Not copied onto the session: enrolment is not drawn (`GAPS.md` B9). */
  programmes: branchProgrammesSchema,
  roles: z.optional(z.array(roleSchema)),
  /**
   * `GrantedRolesObj.modules` as of 02-10-2026 17:34 UTC: bands of
   * `{ module, permissions }`. The session flattens the strings and does
   * not copy the module name onto `session.modules`. Catalogue confirmation
   * remains open (`GAPS.md` B5, `DECISIONS.md` 30-09-2026).
   */
  modules: z.optional(modulesPermissionsSchema),
});

const companySchema = z.object({
  companyNameEn: z.optional(z.string()),
  companyNameZh: z.optional(z.string()),
  contractStartDate: z.optional(z.string()),
  contractEndDate: z.optional(z.string()),
});

const userSchema = z.object({
  staffId: z.string(),
  /**
   * `A` or `I` as of 27-09-2026 14:24 UTC. A value outside the enum is a
   * contract error: the document no longer leaves `status` a bare string.
   */
  status: accountStatusSchema,
  /**
   * The two preferences the account carries, both `required` in the document
   * and so both required here. As of 27-09-2026 14:24 UTC `UserObj` enumerates
   * them the same way the preference write does (`EN|ZH`, `BROWSER|WINGRID`).
   * `localeFromDisplayLang` and `viewFromScreenLayout` still map those values;
   * a spelling outside the enum does not reach them.
   */
  displayLang: displayLangSchema,
  screenLayout: screenLayoutSchema,
  /** On `UserObj` as of 24-09-2026. Read only during sign-in, never on a reload. */
  isPasswordExpired: z.optional(z.boolean()),
  /** On `UserObj` as of 24-09-2026 09:36 UTC. The account carries it; no write in the document sets it. */
  isBlocked: z.optional(z.boolean()),
  /** On `UserObj` as of 25-09-2026. The roster's order. Not used to sort here. */
  createDate: z.optional(z.string()),
  staffNameEn: z.optional(z.string()),
  staffNameZh: z.optional(z.string()),
  email: z.optional(z.string()),
  phone: z.optional(z.string()),
  isPharmacist: z.boolean(),
  /** Required on `UserObj` as of 18-10-2026. Password and preference writes send it back. */
  version: z.int().check(z.gte(0)),
  pharmacistRegNumber: z.optional(z.string()),
  employmentFirstDay: z.optional(z.string()),
  employmentLastDay: z.optional(z.string()),
});

const loginPayloadSchema = z.extend(userSchema, {
  company: z.optional(companySchema),
  branches: z.optional(z.array(grantedBranchSchema)),
  /**
   * On `LoginPayload` as of 08-10-2026 02:46 UTC. Optional. Parsed here and
   * not copied onto the session. The branch step preselects a code that
   * names one of several branches and draws that branch first (`login.tsx`).
   */
  lastLoginBranchCode: z.optional(z.string()),
  /** Required here: the interim token is what the reset and the branch exchange send. The document leaves it optional. */
  authToken: z.string(),
});

/**
 * The branch exchange. `authToken` is required here even though the document
 * leaves it optional: a session without a token is not a session. `company`
 * and `branch` stay optional, because the document does not require them.
 */
const loginBranchPayloadSchema = z.extend(userSchema, {
  company: z.optional(companySchema),
  branch: z.optional(grantedBranchSchema),
  authToken: z.string(),
});

/** The profile read. Same shape, and `authToken` is optional there. */
const profileSchema = z.extend(userSchema, {
  company: z.optional(companySchema),
  branch: z.optional(grantedBranchSchema),
  authToken: z.optional(z.string()),
});

export type LoginPayload = z.infer<typeof loginPayloadSchema>;
export type LoginBranchPayload = z.infer<typeof loginBranchPayloadSchema>;
export type GrantedBranch = z.infer<typeof grantedBranchSchema>;
export type UserProfile = z.infer<typeof profileSchema>;

/* ── the calls ───────────────────────────────────────────────────────────── */

export interface Credentials {
  /** The company id. The document calls it `tenantId` and says it is the company id. */
  tenantId: string;
  /** The document's name for the staff id, as of 24-09-2026. */
  staffId: string;
  password: string;
}

/** Step 1. Proves who you are. The token it returns opens only steps 2 and 3. */
export async function login(credentials: Credentials): Promise<LoginPayload> {
  const body = {
    ...credentials,
    password: await encryptPassword(credentials.password, { stamp: new Date() }),
  };
  const { data, response } = await client().POST("/v1/user/login", { body });
  return payloadOf(loginPayloadSchema, data, "/v1/user/login", response.status);
}

/**
 * Step 2, only when step 1 answered `isPasswordExpired`. Spends the one-time
 * password that is already proven by the interim token, so the new one is the
 * whole body.
 */
export async function resetExpiredPassword(newPassword: string): Promise<LoginPayload> {
  const { data, response } = await client().POST("/v1/user/login/reset-password", {
    body: { newPassword: await encryptPassword(newPassword) },
  });
  return payloadOf(loginPayloadSchema, data, "/v1/user/login/reset-password", response.status);
}

/** Step 3. Binds the session to one branch and returns the working token, plus the company and that branch when the server sends them. */
export async function chooseBranch(branchCode: string): Promise<LoginBranchPayload> {
  const { data, response } = await client().POST("/v1/user/login/branches/{branchCode}", {
    params: { path: { branchCode } },
  });
  return payloadOf(loginBranchPayloadSchema, data, "/v1/user/login/branches/{branchCode}", response.status);
}

/** Ends the session server-side. The caller clears the token whatever this answers. */
export async function logout(): Promise<void> {
  await client().POST("/v1/user/logout", {});
}

/**
 * The account behind the current token, and — when the server sends them —
 * the company and the chosen branch. A branch may carry `modules`,
 * bands of permission strings. The session flattens those grants exactly,
 * and there is still no expiry
 * (`docs/features/settings/GAPS.md` B5). `isPasswordExpired` on this
 * read is not a reason to open the new-password step.
 */
export async function readProfile(): Promise<UserProfile> {
  const { data, response } = await client().GET("/v1/user/profile", {});
  return payloadOf(profileSchema, data, "/v1/user/profile", response.status);
}

/** Changes a password the operator already knows. Not part of signing in. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  version: number,
): Promise<void> {
  const [current, next] = await Promise.all([encryptPassword(currentPassword), encryptPassword(newPassword)]);
  await client().PUT("/v1/user/password", { body: { currentPassword: current, newPassword: next, version } });
}

/**
 * The two preferences that belong to the PERSON — their language and their
 * screen layout.
 *
 * BOTH ARE SENT EVERY TIME BECAUSE THE DOCUMENT REQUIRES BOTH. There is no way
 * to say "change my language and leave my layout alone", so every caller has
 * to know the current value of the one it is not changing and send it back
 * unchanged. `version` travels with that pair: it is the lock loaded with
 * this account (18-10-2026), not a third preference. Two terminals changing
 * different preferences in the same minute can still race; the second save
 * carries the lock it loaded, and a stale lock is the server's refusal.
 *
 * Accepted as it stands (`GAPS.md` B6, 21-09-2026: "fine, and absorbed").
 * These are one person's own two settings, changed by hand, so the race needs
 * the same person at two terminals in the same second. A re-read before every
 * write would double the requests to narrow a race it still could not close,
 * so the caller passes the whole pair, and `app-header.tsx` takes the one it
 * is not changing off the session it already holds.
 */
export async function savePreference(preference: {
  displayLang: DisplayLang;
  screenLayout: ScreenLayout;
  /** The lock loaded with this account. */
  version: number;
}): Promise<void> {
  await client().PUT("/v1/user/preference", { body: preference });
}
